import { ZERO, SettlePnlMode } from '@velocity-exchange/sdk';
import { VersionedTransaction } from '@solana/web3.js';
import { useCallback } from 'react';
import useDriftClient from '../useDriftClient';
import useDriftClientIsReady from '../useDriftClientIsReady';
import { useGetTxParams } from '../driftActions/transactions/useGetTxParams';
import { notify } from 'src/utils/notifications';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useAutoSignDriftActionWithFallback } from '../wallets/useAutoSignDriftActionWithFallback';
import { FeePayerError } from 'src/constants/errors/FeePayerError';
import { useTxErrorHandler } from '../driftActions/transactions/useTxErrorHandler';
import { createSettlePnlIx } from '@velocity-exchange/common';
import invariant from 'tiny-invariant';
import { usePositionsByUserKey } from '../query/accountTradingData/usePositions';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';

const SPLIT_THRESHOLD = 4;

const useSettlePnls = (userKey?: string) => {
	const driftClient = useDriftClient();
	const driftClientIsReady = useDriftClientIsReady();
	const targetUserAccountData = useDriftAccountStore((s) =>
		userKey ? s.accounts[userKey] : s.getCurrentUserAccount()
	);

	const getTxParams = useGetTxParams();
	const handleTxError = useTxErrorHandler();

	const settlePnls = useAutoSignDriftActionWithFallback(
		useCallback(
			async (
				signerWallet: UiEnhancedIWalletV2,
				isAutoSigned: boolean,
				toastId: string,
				...marketIndexes: number[]
			) => {
				const userClient = targetUserAccountData?.client;
				invariant(userClient, 'User client is required');
				invariant(
					driftClient && driftClientIsReady,
					'Drift client is required'
				);

				if (!marketIndexes?.length) {
					notify({
						type: 'error',
						message: 'No open positions with PNL to settle',
					});
					return;
				}

				notify({
					id: toastId,
					type: isAutoSigned ? 'awaiting' : 'awaiting_wallet_confirmation',
					message: isAutoSigned ? 'Settling positions...' : undefined,
				});

				try {
					const txParams = getTxParams({
						simulatedComputeUnitMultiplier: 1.6,
						useSimulatedComputeUnits: true,
					});

					// Split into 2 transactions if more than 4 market indexes
					if (marketIndexes.length > SPLIT_THRESHOLD) {
						const midpoint = Math.ceil(marketIndexes.length / 2);
						const firstHalf = marketIndexes.slice(0, midpoint);
						const secondHalf = marketIndexes.slice(midpoint);

						const [firstIxs, secondIxs] = await Promise.all([
							createSettlePnlIx({
								velocityClient: driftClient,
								user: userClient,
								marketIndexes: firstHalf,
								mode: SettlePnlMode.TRY_SETTLE,
								mainSignerOverride: signerWallet.publicKey,
							}),
							createSettlePnlIx({
								velocityClient: driftClient,
								user: userClient,
								marketIndexes: secondHalf,
								mode: SettlePnlMode.TRY_SETTLE,
								mainSignerOverride: signerWallet.publicKey,
							}),
						]);

						const { signedTxMap } =
							await signerWallet.buildAndSignTransactionsMap(
								{
									settlePnl1: firstIxs,
									settlePnl2: secondIxs,
								},
								txParams
							);

						// Send both transactions in parallel
						await Promise.all([
							driftClient.sendTransaction(
								signedTxMap.settlePnl1 as VersionedTransaction,
								undefined,
								undefined,
								true
							),
							driftClient.sendTransaction(
								signedTxMap.settlePnl2 as VersionedTransaction,
								undefined,
								undefined,
								true
							),
						]);
					} else {
						const ixs = await createSettlePnlIx({
							velocityClient: driftClient,
							user: userClient,
							marketIndexes,
							mode: SettlePnlMode.TRY_SETTLE,
							mainSignerOverride: signerWallet.publicKey,
						});

						await signerWallet.signAndSendTransaction(ixs, txParams);
					}

					notify({
						id: toastId,
						type: 'success',
						message: 'Settled positions',
					});
				} catch (error) {
					console.error('Error settling multiple pnl', error);
					if (error instanceof FeePayerError) {
						throw error;
					}

					handleTxError(error, toastId);
				}
			},
			[
				driftClient,
				driftClientIsReady,
				targetUserAccountData?.client,
				getTxParams,
				handleTxError,
			]
		)
	);

	const { data: openPerpPositionsResult } = usePositionsByUserKey(
		targetUserAccountData?.userKey
	);

	const settleAllPnls = useCallback(async () => {
		if (!openPerpPositionsResult) return;

		const marketIndexesWithPnl = openPerpPositionsResult.openPerpPositions
			.filter((pos) => !pos.unsettledPnl.eq(ZERO))
			.map((pos) => pos.marketIndex);

		return settlePnls(`settle-all-pnls-${Date.now()}`, ...marketIndexesWithPnl);
	}, [settlePnls, openPerpPositionsResult]);

	return { settleAllPnls, settlePnls };
};

export default useSettlePnls;
